import React, { Fragment, useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import produce from 'immer';
import { useMutation } from 'react-apollo';

import { getErrorString } from '../../../util/normalize-errors';
import Alert from '../Alert';
import Button from '../Button';
import { MeQuery } from '../../../graphql/queries/account';
import { updateSelfie } from '../../../apps/admin/graphql/mutations/identity';
import SelfieCaptureContainer from '../selfie-capture/SelfieCapture';
import { fileInputAccept } from '../../../constants/photo-upload';
import { photoUploadFilter } from '../../../util/photo-upload';
import AccountIdentitySelfieConfirm from '../../../apps/consumer/components/AccountIdentitySelfieConfirm';
import useOpenModal from '../../../hooks/useOpenModal';
import Spinner from '../Spinner';
import CameraIcon from '../../icons/CameraIcon';
import { STEPS, DEFAULT_THUMBNAIL_SIZES } from './constants';
import usePhotosUpload from '../../../hooks/usePhotosUpload';
import { isPrimaryIdentity } from '../../../util/identity';

const propTypes = {
  user: PropTypes.shape({
    id: PropTypes.string,
  }),
  identity: PropTypes.shape({
    id: PropTypes.string,
    relationship: PropTypes.string,
  }),
  onSuccess: PropTypes.func,
  uploadOnly: PropTypes.bool,
  onRequestClose: PropTypes.func,
  actionProps: PropTypes.shape({
    identity: PropTypes.shape({
      id: PropTypes.string,
      relationship: PropTypes.string,
    }),
  }),
};

const UpdateSelfieForm = ({
  user,
  onSuccess,
  uploadOnly,
  onRequestClose,
  actionProps = {},
}) => {
  const { identity } = actionProps;
  const [loading, setLoading] = useState(false);
  const [step, setStep] = useState(STEPS.SELFIE);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState('');
  const [selfieMode, setSelfieMode] = useState(false);
  const [file, setFile] = useState(null);
  const [fileInputRef, setFileInputRef] = useState(null);
  const [fileUrl, setFileUrl] = useState(null);
  const [uploadedFile, setUploadedFile] = useState(null);
  const openModal = useOpenModal();
  const isPrimaryIdentityBool = isPrimaryIdentity(identity);
  const hasPhoto = !!identity?.current_photo;

  const [updateSelfieMutation] = useMutation(updateSelfie, {
    update: store => {
      if (uploadedFile && isPrimaryIdentityBool) {
        const data = store.readQuery({
          query: MeQuery,
        });

        const updatedData = produce(data, draft => {
          draft.me = {
            ...data.me,
            avatar_photo: {
              __typename: 'AvatarPhoto',
              // Override the typename if available.
              ...data.me.avatar_photo,
              id: uploadedFile.id,
              thumbnail_urls: (
                data.me.avatar_photo?.thumbnail_urls ?? DEFAULT_THUMBNAIL_SIZES
              ).map(thumbnail => ({ ...thumbnail, url: uploadedFile.url })),
            },
          };
        });

        store.writeQuery({
          query: MeQuery,
          data: updatedData,
        });
      }
      //  Reset uploaded file
      setUploadedFile(null);
    },
  });

  useEffect(() => {
    setSuccess(false);
    setError('');
    setSelfieMode(false);
  }, [step]);

  const handleSelfieUpdate = async photoId => {
    if (identity) {
      try {
        await updateSelfieMutation({
          variables: {
            input: {
              identity_id: identity.id,
              photo_id: photoId,
              selfie_verify: true,
              is_avatar: true,
            },
          },
        });

        setStep(STEPS.SELFIE);
        setError('');
        setSuccess(true);
        onSuccess?.(uploadedFile);
        onRequestClose();
      } catch ({ message }) {
        setError(getErrorString(message));
        setSuccess(false);
      } finally {
        setLoading(false);
      }
    } else {
      setError('No identities found for this user.');
      setLoading(false);
    }
  };

  const { onPhotoUpload } = usePhotosUpload({
    onPhotoUploadSuccess: ({ file: upload, photoId }) => {
      setUploadedFile({ file: upload, id: photoId, url: fileUrl });
      if (uploadOnly) {
        onSuccess?.({ file: upload, id: photoId, url: fileUrl });
        onRequestClose();
      } else {
        handleSelfieUpdate(photoId);
      }
    },
  });

  const onSelfieConfirm = async () => {
    setSuccess(false);
    setLoading(true);
    try {
      // Account for the possibility of the upload erring out.
      const response = await onPhotoUpload({
        files: [file],
        accountId: user.id,
        selfie_verify: true,
      });
      if (response.error) {
        setError(response.error);
        setLoading(false);
      }
    } catch ({ message }) {
      setError(getErrorString(message));
      setSuccess(false);
      setLoading(false);
    }
  };

  const onFileInputChange = async event => {
    const [submittedFile] = event.target.files;

    if (!photoUploadFilter(submittedFile)) {
      setFile(null);
      return;
    }
    setFile(submittedFile);
    setFileUrl(URL.createObjectURL(submittedFile));
    setStep(STEPS.SELFIE_CONFIRM);
  };

  return (
    <Fragment>
      <h3 className="w-100 text-center mb-4">
        {identity && !isPrimaryIdentityBool && (
          <>
            {hasPhoto ? 'Update' : 'Add'} Photo of{' '}
            <span className="text-primary">{identity.name}</span>
          </>
        )}
        {identity && isPrimaryIdentityBool && (
          <>
            {hasPhoto ? 'Update' : 'Add'}{' '}
            <span className="text-primary">your</span> photo!
          </>
        )}
        {!identity && <>Add New Photo</>}
      </h3>
      {step === STEPS.SELFIE_CONFIRM ? (
        <AccountIdentitySelfieConfirm
          photoUrl={fileUrl}
          onSelfieConfirm={onSelfieConfirm}
          onSelfieReject={() => setStep(STEPS.SELFIE)}
          openModal={openModal}
          skipManualCheck
          buttonsDisabled={loading}
        />
      ) : (
        <div className="d-flex flex-column justify-content-center">
          <div className="update-selfie-container">
            <SelfieCaptureContainer
              crop={false}
              includeTipsCopy={false}
              onSelfieConfirmed={data => {
                setFile(data);
                setFileUrl(URL.createObjectURL(data));
                setStep(STEPS.SELFIE_CONFIRM);
              }}
              onError={err => {
                setError(getErrorString(err));
                setSelfieMode(false);
              }}
              onStreamStarted={() => {
                setSelfieMode(true);
              }}
              storeKey="selfie-capture-app"
              countdown={3} // secs
              renderPermissionButton={({
                loadingDevices,
                getCameraPermission,
              }) => {
                if (!loadingDevices) {
                  return null;
                }
                return (
                  <Button
                    onClick={() => {
                      getCameraPermission();
                    }}
                    className="mt-2 px-4"
                  >
                    Activate My Camera
                  </Button>
                );
              }}
              renderCaptureAction={onClick => (
                <Button className="selfie-capture-action-btn" onClick={onClick}>
                  <CameraIcon
                    color="white"
                    id="capture-btn-camera-icon"
                    size={48}
                  />
                </Button>
              )}
              faceMaskDisabled
            />
          </div>
          {selfieMode && (
            <Fragment>
              <Button
                btnStyle="link"
                className="my-2 text-extra-bold"
                onClick={() => {
                  setFile(null);
                  setFileUrl(null);
                  fileInputRef.value = null;
                  fileInputRef.click();
                }}
              >
                Upload a photo instead
              </Button>
              <div className="mb-0 mx-auto">
                <h4 className="text-center w-100">Snap Tips</h4>
                <ol>
                  <li>Make sure the photo is well-lit and straight on.</li>
                  <li>Remove sunglasses and hats.</li>
                  <li>Give us a big smile!</li>
                </ol>
              </div>
            </Fragment>
          )}
          {!selfieMode && (
            <Button
              btnStyle="link"
              className="my-3 text-extra-bold"
              onClick={() => {
                setFile(null);
                setFileUrl(null);
                fileInputRef.value = null;
                fileInputRef.click();
              }}
            >
              Upload a photo instead
            </Button>
          )}
        </div>
      )}
      <input
        id="upload-photo"
        type="file"
        accept={fileInputAccept}
        onChange={onFileInputChange}
        ref={ref => {
          setFileInputRef(ref);
        }}
        hidden
      />
      {loading && (
        <div className="w-100 mt-2 d-flex justify-content-center">
          <Spinner />
        </div>
      )}
      {error && (
        <div className="w-100 mt-2">
          <Alert type="danger" className="w-100">
            <p className="text-bold">Some issues with your data:</p>
            <ul className="mb-0">
              <li key={error}>{error}</li>
            </ul>
          </Alert>
        </div>
      )}
      {success && (
        <div className="w-100 mt-2">
          <Alert type="success" className="w-100 text-center">
            Your selfie has been successfully updated!
          </Alert>
        </div>
      )}
    </Fragment>
  );
};

UpdateSelfieForm.propTypes = propTypes;

export default UpdateSelfieForm;
